import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';

// Module-scope tracker survives component teardown so we can detect a
// remount that follows a recent unmount in the SAME JS context (the
// signature of a React StrictMode double-mount or a fast re-render that
// recreates the tree). It does NOT survive a full page reload — that case
// is detected separately via performance.navigation type='reload'.
const lastUnmountByComponent = new Map();

const RAPID_REMOUNT_THRESHOLD_MS = 2000;

// Page reloads on auth pages are common (refresh, tab restore, deep link,
// back button) and don't reliably indicate a problem on their own. Gate
// Sentry captureMessage events behind a small sample rate so we still get
// signal in the dashboard without inflating quota / alert noise. Keep
// breadcrumbs at 100% — they're passive and only attach to other events.
const RELOAD_SAMPLE_RATE = 0.1;

const getNavigationType = () => {
    try {
        const nav = performance.getEntriesByType('navigation')[0];
        return nav?.type ?? 'unknown';
    } catch {
        return 'unavailable';
    }
};

/**
 * Lifecycle observability for auth pages (Clerk SignIn/SignUp wrappers).
 *
 * We've seen duplicate verification-code emails during signup. The two known
 * causes both manifest as the auth component being mounted more than once:
 *   1. In-context double-mount — React StrictMode or a parent re-render
 *      recreating the tree. Detected by a remount that follows a recent
 *      unmount within RAPID_REMOUNT_THRESHOLD_MS.
 *   2. Page refresh — the user (or some redirect) reloads mid-flow.
 *      Detected by performance navigation type='reload' on mount.
 *
 * Events emit to console (visible in browser devtools in prod) and Sentry
 * (breadcrumbs always; an active warning captureMessage only when a rapid
 * remount fires). No PII is logged — only synthetic instance IDs and
 * timing data.
 */
export const useAuthLifecycleLogging = (componentName) => {
    const instanceIdRef = useRef(
        `${componentName}-${performance.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    );

    useEffect(() => {
        const instanceId = instanceIdRef.current;
        const mountTime = performance.now();
        const navigationType = getNavigationType();
        const lastUnmount = lastUnmountByComponent.get(componentName) ?? null;
        const timeSinceLastUnmountMs = lastUnmount != null ? mountTime - lastUnmount : null;
        const isRapidRemount =
            timeSinceLastUnmountMs != null &&
            timeSinceLastUnmountMs < RAPID_REMOUNT_THRESHOLD_MS;
        const isReload = navigationType === 'reload';

        const mountInfo = {
            component: componentName,
            instanceId,
            mountTime,
            navigationType,
            timeSinceLastUnmountMs,
            isRapidRemount,
            isReload,
        };

        // eslint-disable-next-line no-console
        console.info('[AuthLifecycle] mount', mountInfo);
        Sentry.addBreadcrumb({
            category: 'auth.lifecycle',
            level: 'info',
            message: `${componentName} mount`,
            data: mountInfo,
        });

        if (isRapidRemount) {
            // eslint-disable-next-line no-console
            console.warn(
                `[AuthLifecycle] RAPID REMOUNT — ${componentName} mounted ${timeSinceLastUnmountMs}ms after a previous unmount. This is a likely cause of duplicate verification emails.`
            );
            Sentry.captureMessage(
                `${componentName} rapid remount (${timeSinceLastUnmountMs}ms after unmount)`,
                {
                    level: 'warning',
                    tags: { auth_lifecycle: 'rapid_remount', component: componentName },
                    extra: mountInfo,
                }
            );
        }

        if (isReload) {
            // eslint-disable-next-line no-console
            console.info(
                `[AuthLifecycle] PAGE RELOAD on ${componentName} — refresh mid-flow can re-trigger verification email send.`
            );
            // Reloads are common (refresh, tab restore, deep link, back
            // button) and most don't indicate a problem. Sample at
            // RELOAD_SAMPLE_RATE so we still get signal without inflating
            // Sentry quota / alert noise. Use level='info' (not 'warning')
            // so this doesn't trigger alert rules built on warning-or-above.
            // The breadcrumb above always fires — it's free context for
            // any other Sentry event captured later in the session.
            if (Math.random() < RELOAD_SAMPLE_RATE) {
                Sentry.captureMessage(
                    `${componentName} mounted via page reload`,
                    {
                        level: 'info',
                        tags: { auth_lifecycle: 'page_reload', component: componentName },
                        extra: mountInfo,
                    }
                );
            }
        }

        const handleBeforeUnload = () => {
            const unloadInfo = {
                component: componentName,
                instanceId,
                timeOnPageMs: performance.now() - mountTime,
            };
            // eslint-disable-next-line no-console
            console.info('[AuthLifecycle] beforeunload', unloadInfo);
            Sentry.addBreadcrumb({
                category: 'auth.lifecycle',
                level: 'info',
                message: `${componentName} beforeunload`,
                data: unloadInfo,
            });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            const unmountTime = performance.now();
            lastUnmountByComponent.set(componentName, unmountTime);
            const unmountInfo = {
                component: componentName,
                instanceId,
                unmountTime,
                timeMountedMs: unmountTime - mountTime,
            };
            // eslint-disable-next-line no-console
            console.info('[AuthLifecycle] unmount', unmountInfo);
            Sentry.addBreadcrumb({
                category: 'auth.lifecycle',
                level: 'info',
                message: `${componentName} unmount`,
                data: unmountInfo,
            });
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [componentName]);
};
